import React, { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Label from '@components/inputs/text/Label';
import DropDownMenu, { IChoice } from "@components/inputs/DropDownMenu";
import RadioInput from "@components/inputs/RadioInput";
import { NavBar } from "@components/navigation/NavBar/NavBar";
import SubmitBtn from "@components/buttons/FormButtons/SubmitBtn";
import { OnboardingInsuranceState, OnboardingInsuranceAction } from './onboardingInsuranceReducer';
import { OnboardingPersonalState } from './onboardingPersonalReducer';
import { OnboardingAddressState } from './onboardingAddressReducer';
import { OnboardingDriverLicenceState } from './onboardingDriverLicenceReducer';
import { OnboardingEmergencyContactsState } from './onboardingEmergencyContactsReducer';
import { OnboardingAgreementState } from './onboardingAgreementReducer';
import { Country } from "@utils/constants/localisation";
import { ipbCouponChoicesUK, ipbCouponChoicesAU } from "@utils/insurancePriceBeatOptions";
import { addWeeks, countrySpecificCurrency, dateToISOFormat } from '@utils/misc/functions';

interface FlexiOwnInsurancePriceBeatV2Props {
  navigateTarget: string;
  onboardingInsuranceState: OnboardingInsuranceState;
  onboardingInsuranceDispatch: React.Dispatch<OnboardingInsuranceAction>;
  onboardingPersonalState: OnboardingPersonalState;
  onboardingAddressState: OnboardingAddressState;
  onboardingDriverLicenceState: OnboardingDriverLicenceState;
  onboardingEmergencyContactsState: OnboardingEmergencyContactsState;
  onboardingAgreementState: OnboardingAgreementState;
  country: Country;
  custId: string;
}

export default function FlexiOwnInsurancePriceBeatV2({
  navigateTarget,
  onboardingInsuranceState,
  onboardingInsuranceDispatch,
  onboardingPersonalState,
  onboardingAddressState,
  onboardingDriverLicenceState,
  onboardingEmergencyContactsState,
  onboardingAgreementState,
  country,
  custId
}: FlexiOwnInsurancePriceBeatV2Props) {
  const navigate = useNavigate();
  const [hasInsurancePriceBeat, setHasInsurancePriceBeat] = useState<boolean>(false);
  // Default to full weekly fee so the breakdown shows a £0.00 discount until a coupon is picked
  const [ipbDiscountedWeeklyFee, setIpbDiscountedWeeklyFee] = useState<number>(onboardingAgreementState.planWeeklyFee);

  const couponChoices = country === Country.GB ? ipbCouponChoicesUK : ipbCouponChoicesAU;
  const subsStartDate: Date = new Date(onboardingAgreementState.subsStartDate);
  const discountStartDate = dateToISOFormat(subsStartDate);
  const discountEndDate = addWeeks(subsStartDate, 52);

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hasIPB = e.currentTarget.value === 'true';
    setHasInsurancePriceBeat(hasIPB);
    onboardingInsuranceDispatch({ type: "SET_HAS_OTHER_INSURANCE", payload: e.currentTarget.value });

    if (!hasIPB) {
      onboardingInsuranceDispatch({
        type: "SET_ALL",
        payload: {
          insuranceProvider: '',
          insurancePolicyNumber: '',
          insuranceExpiryDate: '',
          insurancePremium: '',
          couponCode: '',
        }
      });
    }
    // Reset to full fee whenever the radio is toggled so the breakdown starts
    // at "no discount yet" (discount = -£0.00, total = planWeeklyFee) rather than
    // "-planWeeklyFee" / "£0". handleCouponChange updates it once a coupon is picked.
    setIpbDiscountedWeeklyFee(onboardingAgreementState.planWeeklyFee);
  };

  const handleCouponChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOption = couponChoices.find(choice => choice.value === e.currentTarget.value);
    if (selectedOption) {
      const discount = parseFloat(selectedOption.value);
      const discountedFee = onboardingAgreementState.planWeeklyFee - discount;
      setIpbDiscountedWeeklyFee(discountedFee);
      onboardingInsuranceDispatch({ type: "SET_COUPON_CODE", payload: selectedOption.label });
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    // TODO: Here you would submit all the collected data to the backend
    // For now, just navigate to the next step
    console.log('Submitting onboarding data:', {
      personal: onboardingPersonalState,
      address: onboardingAddressState,
      driverLicence: onboardingDriverLicenceState,
      emergencyContacts: onboardingEmergencyContactsState,
      agreement: onboardingAgreementState,
      insurance: onboardingInsuranceState
    });
    navigate(navigateTarget);
  };

  const rowStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    width: "100%",
  };

  const itemStyle: React.CSSProperties = {
    flex: 1,
    padding: "10px",
    boxSizing: "border-box",
  };

  const textStyle: React.CSSProperties = {
    ...itemStyle,
    fontWeight: "bold",
  };

  const numberStyle: React.CSSProperties = {
    ...itemStyle,
    textAlign: "right",
  };

  return (
    <>
      <NavBar pageTitle={"Insurance Price Beat"} backAction={undefined} />
      <form style={{ margin: '2.5vh 2.5vw' }} onSubmit={onSubmit}>
        <div style={{ margin: '2.5vh 0' }}>
          <p>
            Does the customer have an existing <strong>comprehensive car insurance</strong> policy with another provider that they would like to transfer to their Splend vehicle?
          </p>
        </div>

        <div style={{ margin: '2.5vh 0' }}>
          <RadioInput
            radioName={'hasOtherInsurance'}
            options={[
              { label: 'Yes', id: 'hasInsurance_yes', value: 'true' },
              { label: 'No', id: 'hasInsurance_no', value: 'false' },
            ]}
            selectedValue={String(hasInsurancePriceBeat)}
            onSelect={handleRadioChange}
            required={true}
          />
        </div>

        {hasInsurancePriceBeat && (
          <>
            <div style={{ margin: '2.5vh 0' }}>
              <Label text={'Insurance coupon code'} requiredFieldInd={true} styleCfg={{ largeFont: false, bold: false }} />
              <DropDownMenu
                menuName='couponCode'
                defaultVal={undefined}
                required={true}
                choices={couponChoices}
                onSelect={handleCouponChange}
              />
            </div>

            <div style={{ margin: '2.5vh 0', border: '1px solid #C4C4C4', borderRadius: '10px', padding: '20px' }}>
              <div style={rowStyle}>
                <div style={textStyle}>Weekly subscription</div>
                <div style={numberStyle}>{countrySpecificCurrency(country)}{onboardingAgreementState.planWeeklyFee.toFixed(2)}</div>
              </div>
              <div style={rowStyle}>
                <div style={textStyle}>Insurance Price Beat discount</div>
                <div style={numberStyle}>
                  -{countrySpecificCurrency(country)}
                  {(onboardingAgreementState.planWeeklyFee - ipbDiscountedWeeklyFee).toFixed(2)}
                </div>
              </div>
              <div style={{ ...rowStyle, borderTop: '2px solid #000', marginTop: '10px', paddingTop: '10px' }}>
                <div style={{ ...textStyle, fontSize: '1.2em' }}>Weekly subscription with discount</div>
                <div style={{ ...numberStyle, fontSize: '1.2em', fontWeight: 'bold' }}>
                  {countrySpecificCurrency(country)}{ipbDiscountedWeeklyFee.toFixed(2)}
                </div>
              </div>
              <div style={{ marginTop: '10px', fontSize: '0.9em', color: '#666' }}>
                <p>Discount period: {discountStartDate} to {dateToISOFormat(discountEndDate)}</p>
              </div>
            </div>
          </>
        )}

        <SubmitBtn text={"Submit"} />
      </form>
    </>
  );
}